import { Box, Paper } from '@mui/material';
import { Bar, BarDatum, ComputedDatum } from '@nivo/bar';
import useComponentSize from '@rehooks/component-size';
import { useMemo, useRef, useState } from 'react';

import { PersonnelReportData } from '~/data/reports';
import { analyticsColors } from '~/scenes/PortalModules/Analytics/common/colors';

import { usePersonnelReportTables } from '../../usePersonnelReportTables';
import { ChartTable } from '../ChartTable/component';

import { useSxStyles } from './useStyles';

interface BarChartProps {
  data: BarDatum[];
  tableData: Record<
    string,
    { count: number; tableData: PersonnelReportData[] }
  >;
  label: string;
  keys: string[];
  indexBy: string;
  selectedSlice: ComputedDatum<BarDatum> | null;
  setSelectedSlice: (slice: ComputedDatum<BarDatum> | null) => void;
  omitColumns?: string[];
  exportFileNamePrefix?: string;
}

export const BarChart = (props: BarChartProps) => {
  const {
    data,
    tableData,
    label,
    keys,
    indexBy,
    selectedSlice,
    setSelectedSlice,
    omitColumns,
    exportFileNamePrefix,
  } = props;

  const [tablePosition, setTablePosition] = useState<{
    top: number;
    left: number;
  }>();
  const [selectedBar, setSelectedBar] =
    useState<ComputedDatum<BarDatum> | null>();

  const sxStyles = useSxStyles();

  const { rows, columns } = usePersonnelReportTables(
    tableData?.[selectedBar?.indexValue || '']?.tableData || [],
    omitColumns,
  );

  const chartRef = useRef();
  const componentSize = useComponentSize(chartRef);

  const commonBarProps = useMemo(() => {
    return {
      width: componentSize.width > 300 ? componentSize.width : 300,
      height: 300,
      margin: { top: 30, right: 55, bottom: 30, left: 80 },
      padding: 0.2,
      labelTextColor: 'inherit:darker(1.4)',
      labelSkipWidth: 16,
      labelSkipHeight: 16,
      colors: analyticsColors,
    };
  }, [componentSize.width]);

  const calculatePadding = (dataLength: number) => 0.92 - dataLength * 0.12;

  return (
    <Paper sx={sxStyles.container}>
      <Box component="h4" sx={sxStyles.title}>
        {label}
      </Box>

      <Box sx={sxStyles.chart} ref={chartRef}>
        <Bar
          {...commonBarProps}
          keys={keys}
          data={data}
          indexBy={indexBy}
          padding={calculatePadding(data.length)}
          margin={{ top: 30, right: 20, bottom: 30, left: 45 }}
          onClick={(bar, { clientX, clientY }) => {
            if (bar.index === selectedSlice?.index) {
              setSelectedSlice(null);
              setSelectedBar(null);
            } else {
              const layoutAlignmentX =
                window.innerWidth - clientX < 430 ? -420 : 0;
              setTablePosition({
                top: clientY,
                left: layoutAlignmentX + clientX,
              });
              setSelectedSlice(bar);
              setSelectedBar(bar);
            }
          }}
        />
      </Box>

      <ChartTable
        isVisible={
          !!selectedSlice?.indexValue &&
          selectedSlice?.indexValue === selectedBar?.indexValue
        }
        tablePosition={tablePosition}
        columns={columns}
        rows={rows}
        label={selectedSlice?.indexValue || ''}
        onClose={() => {
          setSelectedSlice(null);
          setSelectedBar(null);
        }}
        exportFileNamePrefix={exportFileNamePrefix}
      />
    </Paper>
  );
};
